import React from "react"
import { Helmet } from "react-helmet-async"
import { FileText, Users, UserCheck, Shield, AlertTriangle, Scale, Phone, Globe } from "lucide-react"
const TermsConditions: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Terms & Conditions - Anmol Furniture Bazar</title>
        <meta 
          name="description" 
          content="Read our terms and conditions for using Anmol Furniture Bazar platform and services." 
        />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms & Conditions</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Legal terms governing your use of our platform and services
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-8">
            {/* Document Nature */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <FileText className="w-6 h-6 mr-3 text-primary-600" />
                Document Information
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  This document is an electronic record in terms of Information Technology Act, 2000 and rules 
                  thereunder as applicable. This electronic record is generated by a computer system and does not 
                  require any physical or digital signatures.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  This document is published in accordance with the provisions of Rule 3 (1) of the Information 
                  Technology (Intermediaries guidelines) Rules, 2011 for the domain https://www.anmolfurniturebazar.com/
                </p>
              </div>
            </div>

            {/* Platform Information */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <Globe className="w-6 h-6 mr-3 text-primary-600" />
                Platform Details
              </h2>
              <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-900 mb-3">Platform Owner:</h3>
                <div className="text-blue-800 space-y-1">
                  <p><strong>Company:</strong> Anmol Furniture Bazar</p>
                  <p><strong>Incorporation:</strong> Company incorporated under the Companies Act, 1956</p>
                  <p><strong>Registered Office:</strong> Virar</p>
                  <p><strong>Website:</strong> https://www.anmolfurniturebazar.com/</p>
                </div>
              </div>
            </div>

            {/* Agreement & Acceptance */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <Users className="w-6 h-6 mr-3 text-primary-600" />
                Agreement & Acceptance
              </h2>
              
              <div className="space-y-6">
                <div className="bg-red-50 p-6 rounded-lg border border-red-200">
                  <h3 className="font-semibold text-red-900 mb-3">Important Notice:</h3>
                  <p className="text-red-800">
                    <strong>ACCESSING, BROWSING OR OTHERWISE USING THE PLATFORM INDICATES YOUR AGREEMENT 
                    TO ALL THE TERMS AND CONDITIONS UNDER THESE TERMS OF USE. PLEASE READ THE TERMS OF USE 
                    CAREFULLY BEFORE PROCEEDING.</strong>
                  </p>
                </div>

                <div className="space-y-4">
                  <p className="text-gray-700 leading-relaxed">
                    By using the Platform, you shall be contracting with Anmol Furniture Bazar and these terms 
                    constitute your binding obligations. If you transact on the Platform, you shall be subject 
                    to applicable policies for such transactions.
                  </p>
                  
                  <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                    <p className="text-amber-800">
                      <strong>Modifications:</strong> These Terms of Use can be modified at any time without assigning 
                      any reason. It is your responsibility to periodically review these Terms to stay informed of updates.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* User Obligations */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <UserCheck className="w-6 h-6 mr-3 text-primary-600" />
                User Responsibilities
              </h2>
              
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">You agree to:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start space-x-2">
                      <div className="w-1.5 h-1.5 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Provide true, accurate and complete information during registration</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <div className="w-1.5 h-1.5 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Be responsible for all acts done through your registered account</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <div className="w-1.5 h-1.5 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Pay charges associated with availing the Services</span>
                    </li>
                  </ul>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start space-x-2">
                      <div className="w-1.5 h-1.5 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Use the Platform only for lawful purposes</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <div className="w-1.5 h-1.5 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Independently assess that Services meet your requirements</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <div className="w-1.5 h-1.5 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Not use the Platform for any illegal or forbidden purposes</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Limitations & Disclaimers */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <AlertTriangle className="w-6 h-6 mr-3 text-amber-500" />
                Limitations & Disclaimers
              </h2>
              
              <div className="space-y-6">
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <h3 className="font-semibold text-yellow-900 mb-2">No Warranty:</h3>
                  <p className="text-yellow-800 text-sm">
                    Neither we nor any third parties provide any warranty or guarantee as to the accuracy, timeliness, 
                    performance, completeness or suitability of the information and materials offered on this website 
                    or through the Services, for any specific purpose.
                  </p>
                </div>

                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <h3 className="font-semibold text-red-900 mb-2">Use at Your Own Risk:</h3>
                  <p className="text-red-800 text-sm">
                    Your use of our Services and the Platform is solely and entirely at your own risk and discretion 
                    for which we shall not be liable to you in any manner.
                  </p>
                </div>
              </div>
            </div>

            {/* Intellectual Property */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <Shield className="w-6 h-6 mr-3 text-primary-600" />
                Intellectual Property
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  The contents of the Platform and the Services are proprietary to us and are licensed to us. 
                  You will not have any authority to claim any intellectual property rights, title, or interest in its contents.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  The contents include and are not limited to the design, layout, look and graphics. 
                  Unauthorized use of the Platform and/or the Services may lead to action against you as per 
                  these Terms of Use and/or applicable laws.
                </p>
              </div>
            </div>

            {/* Legal Binding */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <Scale className="w-6 h-6 mr-3 text-primary-600" />
                Legal Binding Contract
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  You understand that upon initiating a transaction for availing the Services, you are entering 
                  into a legally binding and enforceable contract with Anmol Furniture Bazar for the Services.
                </p>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-blue-800">
                    <strong>Third Party Links:</strong> The website and Services may contain links to other third party websites. 
                    On accessing these links, you will be governed by the terms of use, privacy policy and such other policies 
                    of such third party websites.
                  </p>
                </div>
              </div>
            </div>

            {/* Indemnification */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <Shield className="w-6 h-6 mr-3 text-red-500" />
                Indemnification
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed">
                  You shall indemnify and hold harmless Anmol Furniture Bazar, its affiliates, group companies 
                  and their respective officers, directors, agents, and employees, from any claim or demand, or actions 
                  including reasonable attorney's fees, made by any third party or penalty imposed due to or arising out of:
                </p>
                <ul className="text-gray-700 space-y-2 ml-6 mt-4">
                  <li>Your breach of these Terms of Use</li>
                  <li>Your breach of Privacy Policy and other Policies</li>
                  <li>Your violation of any law, rules or regulations</li>
                  <li>Your violation of rights of a third party (including intellectual property rights infringement)</li>
                </ul>
              </div>
            </div>

            {/* Force Majeure */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <AlertTriangle className="w-6 h-6 mr-3 text-amber-500" />
                Force Majeure
              </h2>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <p className="text-amber-800">
                  Notwithstanding anything contained in these Terms of Use, the parties shall not be liable for any 
                  failure to perform an obligation under these Terms if performance is prevented or delayed by a force majeure event.
                </p>
              </div>
            </div>

            {/* Governing Law */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <Scale className="w-6 h-6 mr-3 text-primary-600" />
                Governing Law & Jurisdiction
              </h2>
              
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h3 className="font-semibold text-blue-900 mb-2">Governing Law:</h3>
                  <p className="text-blue-800">
                    These Terms and any dispute or claim relating to it, or its enforceability, shall be governed by 
                    and construed in accordance with the laws of India.
                  </p>
                </div>

                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h3 className="font-semibold text-green-900 mb-2">Exclusive Jurisdiction:</h3>
                  <p className="text-green-800">
                    All disputes arising out of or in connection with these Terms shall be subject to the exclusive 
                    jurisdiction of the courts in Virar, Maharashtra.
                  </p>
                </div>
              </div>
            </div>

            {/* User Definitions */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">Definitions</h2>
              <div className="space-y-4">
                <div className="border-l-4 border-blue-400 pl-4">
                  <h3 className="font-semibold text-gray-900 mb-2">User/You/Your:</h3>
                  <p className="text-gray-700">
                    Any natural or legal person who has agreed to become a user/buyer on the Platform.
                  </p>
                </div>
                <div className="border-l-4 border-green-400 pl-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Platform:</h3>
                  <p className="text-gray-700">
                    The website https://www.anmolfurniturebazar.com/ including related mobile site and mobile application.
                  </p>
                </div>
                <div className="border-l-4 border-purple-400 pl-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Services:</h3>
                  <p className="text-gray-700">
                    Collectively refers to our website, goods (as applicable) and services (as applicable).
                  </p>
                </div>
              </div>
            </div>

            {/* Additional Terms */}
            <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
              <h2 className="text-xl font-semibold text-blue-900 mb-4">Additional Important Terms</h2>
              <ul className="space-y-2 text-blue-800">
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Any terms proposed by you that conflict with these Terms are expressly rejected</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                  <span>We exclude liability for inaccuracies or errors to the fullest extent permitted by law</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                  <span>You are required to independently assess that our Services meet your requirements</span>
                </li>
              </ul>
            </div>

            {/* Contact Section */}
            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <Phone className="w-12 h-12 text-primary-600 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Questions About Terms & Conditions?</h2>
              <p className="text-gray-600 mb-6">
                All concerns or communications relating to these Terms must be communicated to us using the contact information provided on our website
              </p>
              <div className="space-y-2">
                <p className="text-lg"><span className="font-semibold">Website:</span> https://www.anmolfurniturebazar.com/</p>
                <p className="text-gray-600">Contact information available on our website</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default TermsConditions